import React, { useState } from 'react';
import { unifiedApi } from '../../api/unifiedApi';

interface UnlockNoteModalProps {
  noteId: string;
  noteTitle: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const UnlockNoteModal: React.FC<UnlockNoteModalProps> = ({
  noteId,
  noteTitle,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // 处理密码输入
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setPassword(value);
    setError('');
  };

  // 处理确认
  const handleConfirm = async () => {
    if (password.length !== 4) {
      setError('请输入4位数字密码');
      return;
    }

    setIsLoading(true);
    try {
      await unifiedApi.unlockNote(noteId, password);
      onSuccess();
      onClose();
    } catch (err: any) {
      if (err.message?.includes('密码错误')) {
        setError('密码错误，请重试');
      } else {
        setError('取消上锁失败，请重试');
      }
      setPassword('');
    } finally {
      setIsLoading(false);
    }
  };

  // 处理取消
  const handleCancel = () => {
    setPassword('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
        {/* 标题 */}
        <div className="flex items-center mb-4">
          <svg className="w-6 h-6 text-orange-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" strokeWidth="2" />
            <circle cx="12" cy="16" r="1" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" strokeWidth="2" />
          </svg>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            取消笔记上锁
          </h2>
        </div>

        {/* 说明文字 */}
        <div className="mb-6 text-sm text-gray-600 dark:text-gray-300 bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg">
          <p className="mb-1">
            您正在取消笔记「<span className="font-medium text-gray-900 dark:text-white">{noteTitle}</span>」的上锁状态。
          </p>
          <p>
            请输入上锁密码以确认操作。
          </p>
        </div>

        {/* 密码输入 */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            输入4位数字密码
          </label>
          <input
            type="password"
            value={password}
            onChange={handlePasswordChange}
            placeholder="请输入4位数字"
            maxLength={4}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-center text-lg tracking-widest"
          />
        </div>

        {/* 错误提示 */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* 按钮 */}
        <div className="flex gap-3">
          <button
            onClick={handleCancel}
            disabled={isLoading}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            取消
          </button>
          <button
            onClick={handleConfirm}
            disabled={password.length !== 4 || isLoading}
            className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? '验证中...' : '确认取消上锁'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnlockNoteModal;
